import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToastStore } from "@/stores/toastStore";
import { useUsageStore } from "@/stores/usageStore";
import { transcribeAudio } from "@/services/whisper";
import { type ClickedLocation, type DrawnPolygon } from "@/stores/mapStore";

interface ChatInputProps {
  onSend: (content: string) => void;
  disabled: boolean;
  isStreaming?: boolean;
  onStop?: () => void;
  location?: ClickedLocation | null;
  onClearLocation?: () => void;
  drawnPolygon?: DrawnPolygon | null;
  onClearPolygon?: () => void;
  isDrawing?: boolean;
  onDrawToggle?: () => void;
}

type VoiceState = "idle" | "recording" | "transcribing";

export function ChatInput({ onSend, disabled, isStreaming, onStop, location, onClearLocation, drawnPolygon, onClearPolygon, isDrawing: _isDrawing, onDrawToggle: _onDrawToggle }: ChatInputProps) {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const hasLocation = !!location;
  const hasPolygon = !!drawnPolygon;
  const hasContext = hasLocation || hasPolygon;
  const canSend = !disabled && !isStreaming && (value.trim() || hasContext);
  const showMic = !disabled && !isStreaming && voiceState === "idle";


  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSend) return;
    onSend(value.trim());
    setValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape" && isStreaming && onStop) {
      e.preventDefault();
      onStop();
      return;
    }
    if (e.key === "Enter") {
      const sendOnEnter = localStorage.getItem("ortvis-send-on-enter") !== "false";
      const shouldSend = sendOnEnter ? !e.shiftKey : e.shiftKey;
      if (shouldSend) {
        e.preventDefault();
        handleSubmit(e);
      }
    }
  }

  function handleInput() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }

  // ── Voice recording ──

  const mimeTypeRef = useRef("audio/webm");

  const handleRecordingComplete = useCallback(async () => {
    const chunks = chunksRef.current;
    chunksRef.current = [];

    const blob = new Blob(chunks, { type: mimeTypeRef.current });

    if (blob.size < 1000) {
      setVoiceState("idle");
      return;
    }

    setVoiceState("transcribing");

    try {
      const ext = mimeTypeRef.current.includes("mp4") ? "recording.mp4" : "recording.webm";
      const file = new File([blob], ext, { type: mimeTypeRef.current });
      const text = await transcribeAudio(file, i18n.language);
      if (text) {
        setValue(text);
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.style.height = "auto";
            el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
          }
        });
      }
    } catch {
      useToastStore.getState().addToast({ type: "error", message: t("voice.transcriptionFailed") });
    }
    setVoiceState("idle");
  }, [i18n.language, t]);

  async function startRecording() {
    if (voiceState !== "idle") return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      let mimeType = "";
      for (const candidate of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
        if (MediaRecorder.isTypeSupported(candidate)) {
          mimeType = candidate;
          break;
        }
      }
      mimeTypeRef.current = mimeType || "audio/webm";

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = handleRecordingComplete;

      mediaRecorderRef.current = recorder;
      recorder.start();
      setVoiceState("recording");
    } catch {
      useToastStore.getState().addToast({ type: "error", message: t("voice.micDenied") });
    }
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    recorder.stop();
    recorder.stream.getTracks().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
  }

  // Recording timer
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  useEffect(() => {
    if (voiceState !== "recording") {
      setRecordingSeconds(0);
      return;
    }
    const interval = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [voiceState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);


  // ── Right-side button (send or stop) ──

  function renderRightButton() {
    if (isStreaming) {
      return (
        <button
          type="button"
          onClick={onStop}
          className="cx-input-btn cx-input-btn--stop"
          aria-label={t("chat.stop")}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <rect x="4" y="4" width="16" height="16" rx="2" />
          </svg>
          {t("chat.stop")}
        </button>
      );
    }

    if (voiceState === "recording") {
      const mins = Math.floor(recordingSeconds / 60);
      const secs = recordingSeconds % 60;
      return (
        <button
          type="button"
          onClick={stopRecording}
          className="cx-input-btn cx-input-btn--recording"
          aria-label={t("voice.stopRecording")}
        >
          <span className="cx-recording-dot" />
          <span className="cx-recording-time">{mins}:{secs.toString().padStart(2, "0")}</span>
        </button>
      );
    }

    if (voiceState === "transcribing") {
      return (
        <button type="button" disabled className="cx-input-btn cx-input-btn--transcribing" aria-label={t("voice.transcribing")}>
          <svg className="animate-cx-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </button>
      );
    }

    if (canSend) {
      return (
        <button
          type="submit"
          className="cx-input-btn cx-input-btn--ready"
          aria-label={t("chat.send")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      );
    }

    if (showMic) {
      return (
        <button
          type="button"
          onClick={startRecording}
          className="cx-input-btn cx-input-btn--mic"
          aria-label={t("voice.record")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="22" />
          </svg>
        </button>
      );
    }

    return null;
  }

  return (
    <div className="px-4 pb-4 pt-2">
      <form
        onSubmit={handleSubmit}
        className="cx-chat-input"
      >
        {/* Context chips (location or polygon tag) */}
        {hasLocation && (
          <div className="px-4 pt-3 pb-0">
            <span className="cx-location-tag inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-medium">
              <span className="text-[11px]">📍</span>
              {location.name}
              <button
                type="button"
                onClick={onClearLocation}
                className="ml-0.5 text-[11px] opacity-50 transition-opacity hover:opacity-100"
                aria-label={t("chat.removeLocation")}
              >
                ✕
              </button>
            </span>
          </div>
        )}

        {hasPolygon && !hasLocation && (
          <div className="px-4 pt-3 pb-0">
            <span className="cx-location-tag inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-medium">
              <span className="text-[11px]">⬡</span>
              {drawnPolygon.name}
              <button
                type="button"
                onClick={onClearPolygon}
                className="ml-0.5 text-[11px] opacity-50 transition-opacity hover:opacity-100"
                aria-label={t("chat.removeArea")}
              >
                ✕
              </button>
            </span>
          </div>
        )}

        <textarea
          id="chat-input"
          data-chat-input
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={
            voiceState === "recording" ? t("voice.listening")
            : voiceState === "transcribing" ? t("voice.transcribing")
            : hasPolygon ? t("chat.placeholderWithArea")
            : hasLocation ? t("chat.placeholderWithLocation")
            : t("chat.placeholder")
          }
          disabled={disabled && !isStreaming}
          rows={1}
          className="cx-chat-textarea w-full resize-none bg-transparent px-4 py-2 text-[15px] leading-relaxed focus:outline-none disabled:opacity-50"
          style={{ paddingTop: hasContext ? "8px" : "12px" }}
        />

        {/* ── Bottom toolbar: mic/send right ── */}
        <div className="flex items-center justify-end px-3 pb-2">
          {/* Right: mic / send / stop */}
          <div className="flex items-center gap-1">
            {renderRightButton()}
          </div>
        </div>
      </form>
      <div className="text-center mt-1.5">
        <ChatDisclaimer />
      </div>
    </div>
  );
}

function ChatDisclaimer() {
  const { t } = useTranslation();
  const { messagesRemainingToday, messagesLimitToday } = useUsageStore();
  const showRemaining = messagesLimitToday > 0 && messagesRemainingToday <= 2 && messagesRemainingToday > 0;

  return (
    <p className="cx-disclaimer text-[11px]">
      {t("chat.disclaimer")}{showRemaining ? ` · ${t("usage.messagesLeftToday", { count: messagesRemainingToday })}` : ""}
    </p>
  );
}
